import { useEffect } from 'react';
import { useLanguage, LANGUAGES } from '../contexts/LanguageContext';
import { VARIANT_SLUGS } from '../App';

// Helper to get localized slug for a variant
const getLocalizedSlug = (variantKey, lang) => {
  if (variantKey === 'main') return '';
  const langSlugs = VARIANT_SLUGS[lang] || VARIANT_SLUGS['en'];
  return langSlugs[variantKey] || VARIANT_SLUGS['en'][variantKey] || '';
};

// Helper to get variant key from any slug
const getVariantKeyFromSlug = (slug) => {
  if (!slug) return 'main';
  for (const [lang, slugs] of Object.entries(VARIANT_SLUGS)) {
    for (const [variantKey, variantSlug] of Object.entries(slugs)) {
      if (variantSlug === slug) {
        return variantKey;
      }
    }
  }
  return 'main';
};

const SEOHead = ({
  variant = 'main',
  customTitle,
  customDescription,
  customKeywords,
  canonicalPath,
  noIndex = false,
  faqs = [],
  ogImage = null,
  enhancedSchema = null
}) => {
  const { t, language, isRTL } = useLanguage();
  
  useEffect(() => {
    // Get variant data
    const variantData = t(`variants.${variant}`);
    const title = customTitle || variantData?.title || t('site.title');
    const description = customDescription || variantData?.description || t('site.description');
    const keywords = customKeywords || t('site.keywords');
    const siteName = 'wheel.expert';
    const baseUrl = 'https://wheel.expert';
    
    // Construct canonical URL
    const path = canonicalPath || (language === 'en' ? '/' : `/${language}/`);
    const canonicalUrl = `${baseUrl}${path}`;
    
    // Update document title - avoid duplicate suffix
    const titleSuffix = ` | ${siteName}`;
    const cleanTitle = title.endsWith(titleSuffix) || title.endsWith('| wheel.expert') 
      ? title.replace(/\s*\|\s*wheel\.expert\s*$/, '') 
      : title;
    document.title = `${cleanTitle}${titleSuffix}`;
    
    // Update/create meta tags
    const updateMeta = (name, content, isProperty = false) => {
      if (!content) return;
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    // ================================
    // BASIC META TAGS
    // ================================
    updateMeta('description', description);
    updateMeta('keywords', keywords);
    updateMeta('author', 'wheel.expert');
    updateMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    updateMeta('googlebot', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    updateMeta('bingbot', 'index, follow');
    
    // ================================
    // AI CRAWLER DIRECTIVES
    // ================================
    // GPTBot (OpenAI/ChatGPT)
    updateMeta('GPTBot', 'index, follow');
    // Google AI (Bard/Gemini)
    updateMeta('Google-Extended', 'index, follow');
    // Anthropic (Claude)
    updateMeta('anthropic-ai', 'index, follow');
    updateMeta('ClaudeBot', 'index, follow');
    // Common crawlers
    updateMeta('CCBot', 'index, follow');
    // Perplexity
    updateMeta('PerplexityBot', 'index, follow');
    
    // ================================
    // OPEN GRAPH TAGS
    // ================================
    const ogImageUrl = ogImage ? `${baseUrl}${ogImage}` : `${baseUrl}/og-image.png`;
    updateMeta('og:type', canonicalPath?.includes('/blog/') ? 'article' : 'website', true);
    updateMeta('og:site_name', siteName, true);
    updateMeta('og:title', cleanTitle, true);
    updateMeta('og:description', description, true);
    updateMeta('og:url', canonicalUrl, true);
    updateMeta('og:locale', language.replace('-', '_'), true);
    updateMeta('og:image', ogImageUrl, true);
    updateMeta('og:image:width', '1200', true);
    updateMeta('og:image:height', '630', true);
    updateMeta('og:image:alt', `${cleanTitle} - wheel.expert`, true);
    
    // ================================
    // TWITTER CARD TAGS
    // ================================
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:site', '@wheelexpert');
    updateMeta('twitter:creator', '@wheelexpert');
    updateMeta('twitter:title', cleanTitle);
    updateMeta('twitter:description', description);
    updateMeta('twitter:image', ogImageUrl);
    updateMeta('twitter:image:alt', `${cleanTitle} - wheel.expert`);
    
    // ================================
    // ADDITIONAL SEO META TAGS
    // ================================
    updateMeta('application-name', siteName);
    updateMeta('apple-mobile-web-app-title', siteName);
    updateMeta('format-detection', 'telephone=no');
    updateMeta('mobile-web-app-capable', 'yes');
    updateMeta('apple-mobile-web-app-capable', 'yes');
    updateMeta('apple-mobile-web-app-status-bar-style', 'default');
    
    // Geo targeting
    updateMeta('geo.region', 'US');
    updateMeta('geo.placename', 'United States');
    
    // Content rating
    updateMeta('rating', 'General');
    
    // Revisit frequency hint
    updateMeta('revisit-after', '7 days');
    
    // ================================
    // CANONICAL URL
    // ================================
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);
    
    // ================================
    // HTML ATTRIBUTES
    // ================================
    document.documentElement.lang = language;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    
    // ================================
    // HREFLANG TAGS WITH LOCALIZED SLUGS
    // ================================
    // Remove existing hreflang tags
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    
    // Determine what type of page this is and get variant key if applicable
    const pathParts = path.split('/').filter(Boolean);
    let pageType = 'home'; // home, variant, legal, blog, wheels
    let variantKey = null;
    let pageName = null;
    
    // Check if it's a variant page
    if (variant && variant !== 'main') {
      pageType = 'variant';
      variantKey = variant;
    } else if (pathParts.length > 0) {
      const lastPart = pathParts[pathParts.length - 1];
      // Check if it's a legal page
      if (['privacy', 'terms', 'cookies', 'accessibility', 'faq'].includes(lastPart)) {
        pageType = 'legal';
        pageName = lastPart;
      } else if (lastPart === 'wheels') {
        pageType = 'wheels';
      } else if (lastPart === 'blog' || pathParts.includes('blog')) {
        pageType = 'blog';
        pageName = lastPart;
      } else {
        // Try to detect variant from slug
        const possibleVariant = getVariantKeyFromSlug(lastPart);
        if (possibleVariant !== 'main') {
          pageType = 'variant';
          variantKey = possibleVariant;
        }
      }
    }
    
    // Add hreflang tags for supported languages only (21 languages)
    const SUPPORTED_LANGUAGES = LANGUAGES.slice(0, 21); // Only first 21 languages are fully supported
    SUPPORTED_LANGUAGES.forEach(lang => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang.code);
      
      let localizedUrl;
      
      switch (pageType) {
        case 'variant':
          // Get the localized slug for this variant in target language
          const localizedSlug = getLocalizedSlug(variantKey, lang.code);
          if (lang.code === 'en') {
            localizedUrl = `${baseUrl}/${localizedSlug}/`;
          } else {
            localizedUrl = `${baseUrl}/${lang.code}/${localizedSlug}/`;
          }
          break;
          
        case 'legal':
          if (lang.code === 'en') {
            localizedUrl = `${baseUrl}/${pageName}/`;
          } else {
            localizedUrl = `${baseUrl}/${lang.code}/${pageName}/`;
          }
          break;
          
        case 'wheels':
          if (lang.code === 'en') {
            localizedUrl = `${baseUrl}/wheels/`;
          } else {
            localizedUrl = `${baseUrl}/${lang.code}/wheels/`;
          }
          break;
          
        case 'blog':
          if (lang.code === 'en') {
            localizedUrl = pageName === 'blog' ? `${baseUrl}/blog/` : `${baseUrl}/blog/${pageName}/`;
          } else {
            localizedUrl = pageName === 'blog' ? `${baseUrl}/${lang.code}/blog/` : `${baseUrl}/${lang.code}/blog/${pageName}/`;
          }
          break;
          
        case 'home':
        default:
          if (lang.code === 'en') {
            localizedUrl = `${baseUrl}/`;
          } else {
            localizedUrl = `${baseUrl}/${lang.code}/`;
          }
          break;
      }
      
      link.setAttribute('href', localizedUrl);
      document.head.appendChild(link);
    });
    
    // Add x-default hreflang pointing to English version
    const defaultLink = document.createElement('link');
    defaultLink.setAttribute('rel', 'alternate');
    defaultLink.setAttribute('hreflang', 'x-default');
    
    // x-default should point to the English version of this page
    let xDefaultUrl;
    switch (pageType) {
      case 'variant':
        const enSlug = getLocalizedSlug(variantKey, 'en');
        xDefaultUrl = `${baseUrl}/${enSlug}/`;
        break;
      case 'legal':
        xDefaultUrl = `${baseUrl}/${pageName}/`;
        break;
      case 'wheels':
        xDefaultUrl = `${baseUrl}/wheels/`;
        break;
      case 'blog':
        xDefaultUrl = pageName === 'blog' ? `${baseUrl}/blog/` : `${baseUrl}/blog/${pageName}/`;
        break;
      default:
        xDefaultUrl = `${baseUrl}/`;
        break;
    }
    defaultLink.setAttribute('href', xDefaultUrl);
    document.head.appendChild(defaultLink);
    
    // ================================
    // STRUCTURED DATA (JSON-LD)
    // ================================
    // Remove existing schema scripts
    document.querySelectorAll('script[data-schema]').forEach(el => el.remove());

    // Use enhanced schema if provided, otherwise use default
    let schemaGraph;

    if (enhancedSchema && enhancedSchema['@graph']) {
      // Use the enhanced schema directly
      schemaGraph = enhancedSchema['@graph'];
    } else {
      // Fallback to default schema graph
      schemaGraph = [
      // Organization
      {
        '@type': 'Organization',
        '@id': `${baseUrl}/#organization`,
        'name': 'wheel.expert',
        'url': baseUrl,
        'logo': {
          '@type': 'ImageObject',
          'url': `${baseUrl}/icons/icon.svg`,
          'width': 512,
          'height': 512
        },
        'sameAs': [
          'https://twitter.com/wheelexpert'
        ]
      },
      // WebSite
      {
        '@type': 'WebSite',
        '@id': `${baseUrl}/#website`,
        'url': baseUrl,
        'name': siteName,
        'description': t('site.description'),
        'inLanguage': SUPPORTED_LANGUAGES.map(l => l.code),
        'publisher': {
          '@id': `${baseUrl}/#organization`
        },
        'potentialAction': {
          '@type': 'SearchAction',
          'target': {
            '@type': 'EntryPoint',
            'urlTemplate': `${baseUrl}/?search={search_term_string}`
          },
          'query-input': 'required name=search_term_string'
        }
      },
      // WebApplication
      {
        '@type': 'WebApplication',
        '@id': canonicalUrl,
        'name': title,
        'description': description,
        'url': canonicalUrl,
        'applicationCategory': 'UtilitiesApplication',
        'operatingSystem': 'Any',
        'browserRequirements': 'Requires JavaScript',
        'softwareVersion': '2.0.0',
        'offers': {
          '@type': 'Offer',
          'price': '0',
          'priceCurrency': 'USD'
        },
        'featureList': [
          'Cryptographically secure random selection',
          'Web Crypto API (crypto.getRandomValues)',
          '20 wheel variants for different use cases',
          '21 languages supported',
          'Works offline (Progressive Web App)',
          'No account required',
          '100% free to use',
          'Remove winner feature for raffles',
          'Customizable entries and colors',
          'Share wheels via URL',
          'Full keyboard accessibility',
          'WCAG 2.1 AA compliant'
        ],
        'screenshot': `${baseUrl}/screenshot.png`,
        'author': {
          '@id': `${baseUrl}/#organization`
        }
      },
      // BreadcrumbList
      {
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': 'Home',
            'item': baseUrl
          },
          ...(variant !== 'main' ? [{
            '@type': 'ListItem',
            'position': 2,
            'name': title,
            'item': canonicalUrl
          }] : [])
        ]
      }
    ];
    
      // Add FAQPage schema if FAQs are provided (only for default schema)
      if (faqs && faqs.length > 0 && !enhancedSchema) {
        schemaGraph.push({
          '@type': 'FAQPage',
          'mainEntity': faqs.map(faq => ({
            '@type': 'Question',
            'name': faq.question || faq.q,
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': faq.answer || faq.a
            }
          }))
        });
      }
    }
    
    // Create and append schema script
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.setAttribute('data-schema', 'page');
    schemaScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': schemaGraph
    });
    document.head.appendChild(schemaScript);
    
  }, [variant, customTitle, customDescription, customKeywords, canonicalPath, noIndex, faqs, t, language, isRTL]);
  
  return null; // This component only manages document head
};

export default SEOHead;
